import React from 'react';
import { Stack, Typography } from '@mui/material';
import ModernwashListScreen from './modernwash/ModernwashListScreen';
import PaymentStatusBadge from '../../components/PaymentStatusBadge';
import { fetchModernwashLprLogs } from '../../api/modernwash';
import { colors } from '../../design-system/tokens';
import { pick, formatDateTime, formatConfidence } from './modernwash/format';

/**
 * LPR 인식 로그 — ModernWash 원천의 번호판 자동 인식 기록 (read-only).
 * 계약: backend dto LprLogDto =
 *   { plate, washerId, authorized, reason, memberName, productName, confidence, detectedAt }.
 */
const truthy = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  if (typeof value === 'string') {
    return ['true', 'matched', 'success', 'ok', 'authorized', 'member', 'y', '1'].includes(
      value.toLowerCase(),
    );
  }
  return false;
};

const COLUMNS = [
  {
    key: 'detectedAt',
    label: '인식 시각',
    render: (row) => (
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        {formatDateTime(pick(row, ['detectedAt', 'recognizedAt', 'createdAt', 'timestamp']))}
      </Typography>
    ),
  },
  {
    key: 'plate',
    label: '번호판',
    render: (row) => (
      <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {pick(row, ['plate', 'licensePlate', 'plateNumber', 'carNumber'], '-')}
      </Typography>
    ),
  },
  {
    key: 'member',
    label: '회원',
    render: (row) => {
      const name = pick(row, ['memberName', 'ownerName']);
      const product = pick(row, ['productName', 'membershipName']);
      if (!name && !product) {
        return (
          <Typography variant="body2" sx={{ color: colors.text.muted }}>
            비회원
          </Typography>
        );
      }
      return (
        <Stack spacing={0} sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {name || '-'}
          </Typography>
          {product && (
            <Typography variant="caption" sx={{ color: colors.text.muted }}>
              {product}
            </Typography>
          )}
        </Stack>
      );
    },
  },
  {
    key: 'confidence',
    label: '정확도',
    align: 'right',
    render: (row) => (
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatConfidence(pick(row, ['confidence', 'score', 'accuracy']))}
      </Typography>
    ),
  },
  {
    key: 'result',
    label: '결과',
    render: (row) => {
      const ok = truthy(pick(row, ['authorized', 'matched', 'isMember', 'result', 'success']));
      const reason = pick(row, ['reason']);
      return (
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <PaymentStatusBadge label={ok ? '인증' : '미인증'} status={ok ? 'success' : 'neutral'} />
          {!ok && reason && (
            <Typography variant="caption" sx={{ color: colors.text.muted }}>
              {reason}
            </Typography>
          )}
        </Stack>
      );
    },
  },
  {
    key: 'washer',
    label: '세차기',
    render: (row) => (
      <Typography variant="body2" sx={{ color: colors.text.secondary }}>
        {pick(row, ['washerId', 'deviceId', 'kioskId', 'washer'], '-')}
      </Typography>
    ),
  },
];

const LprLogs = () => (
  <ModernwashListScreen
    title="LPR 인식 로그"
    description="번호판 자동 인식(LPR) 기록이에요. ModernWash 원천을 그대로 읽어 최근 인식 순으로 보여줍니다."
    fetcher={fetchModernwashLprLogs}
    columns={COLUMNS}
    getRowKey={(row, index) =>
      pick(row, ['id', '_id', 'logId'], `lpr-${index}-${pick(row, ['detectedAt'], '')}`)
    }
    emptyText="아직 인식 로그가 없어요."
    minWidth={720}
  />
);

export default LprLogs;
